// frontend/src/components/admin/SystemSmsPolicy.jsx
// Sys_admin UI for managing system_sms_policy per entity.
// Controls SMS delivery via the system Twilio provider (.env) for each entity.
// Uses single batch endpoint (1 API call) instead of N+1 pattern.
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import { MessageSquare, Info, Save, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedButton } from '../themed';

/**
 * SystemSmsPolicy Component
 * Allows sys_admin to manage system_sms_policy per entity.
 * This controls which phone numbers can receive SMS via the system provider.
 * Entity provider (custom Twilio) is controlled separately by entity admins.
 */
const SystemSmsPolicy = () => {
    const { t } = useTranslation();
    const { background, text, cn } = useTheme();
    const [entities, setEntities] = useState([]);
    const [policies, setPolicies] = useState({}); // { entityId: 'none'|'us_ca'|'all' }
    const [originalPolicies, setOriginalPolicies] = useState({});
    const [entityChannels, setEntityChannels] = useState({}); // { entityId: { hasCustomProvider, isEnabled } }
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});

    // Load all entities with SMS policy data in a single API call
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Single batch endpoint — 1 API call, 1 DB query (replaces N+1 pattern)
            // Use the real current entity ID for middleware compatibility. The
            // backend route is still authorized by active global membership, but
            // userEntitiesMiddleware rejects pseudo IDs such as 0 before that
            // route-local global guard can run.
            const entityId = localStorage.getItem('entityId');
            const res = await axios.get(`/api/admin/preferences/${entityId}/sms-policy-overview`);
            const data = res.data?.data || [];

            const policyMap = {};
            const channelMap = {};

            data.forEach(entity => {
                policyMap[entity.id] = entity.system_sms_policy || 'none';
                channelMap[entity.id] = {
                    hasCustomProvider: entity.hasCustomProvider || false,
                    isEnabled: entity.isEnabled || false
                };
            });

            setEntities(data);
            setPolicies(policyMap);
            setOriginalPolicies({ ...policyMap });
            setEntityChannels(channelMap);
        } catch (error) {
            toast.error('Failed to load entity data');
            console.error('Error loading system SMS policy data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Save policy for a single entity
    const handleSave = async (entityId) => {
        const newPolicy = policies[entityId];
        if (newPolicy === originalPolicies[entityId]) return;

        setSaving(prev => ({ ...prev, [entityId]: true }));
        try {
            // Uses dynamic role check against preference_definitions.required_role
            await axios.put(`/api/admin/preferences/${entityId}/system-preference`, {
                key: 'system_sms_policy',
                value: newPolicy
            });
            setOriginalPolicies(prev => ({ ...prev, [entityId]: newPolicy }));
            toast.success(`System SMS policy updated for entity ${entityId}`);
        } catch (error) {
            toast.error(`Failed to update policy: ${error.response?.data?.error || error.message}`);
        } finally {
            setSaving(prev => ({ ...prev, [entityId]: false }));
        }
    };

    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-32", text.secondary)}>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loading')}...</span>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className={cn("text-lg font-semibold", text.primary)}>
                        <MessageSquare className="inline w-5 h-5 mr-2" />
                        {t('systemSmsPolicy')}
                    </h3>
                    <p className={cn("text-sm mt-1", text.secondary)}>
                        {t('systemSmsPolicyDescription')}
                    </p>
                </div>
                <ThemedButton variant="outline" onClick={loadData}>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    {t('refresh')}
                </ThemedButton>
            </div>

            {/* Info banner */}
            <div className={cn("p-3 rounded-lg", "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700")}>
                <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className={cn("text-sm", "text-blue-800 dark:text-blue-200")}>
                        {t('systemSmsPolicyDescription')}. Default is &quot;{t('smsPolicyNone')}&quot; — sys_admin must explicitly enable per entity.
                    </p>
                </div>
            </div>

            {/* Entity list */}
            <div className="space-y-3">
                {entities.map(entity => {
                    const policy = policies[entity.id] || 'none';
                    const hasChanged = policy !== originalPolicies[entity.id];
                    const channel = entityChannels[entity.id] || {};

                    return (
                        <div
                            key={entity.id}
                            className={cn(
                                "p-4 rounded-lg border",
                                "border-gray-200 dark:border-gray-700",
                                background.secondary
                            )}
                        >
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                {/* Entity info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className={cn("font-medium text-sm", text.primary)}>
                                            {entity.name}
                                        </span>
                                        <span className={cn("text-xs px-1.5 py-0.5 rounded", "bg-gray-100 dark:bg-gray-700", text.secondary)}>
                                            ID: {entity.id}
                                        </span>
                                        {/* Badge: entity has custom SMS provider */}
                                        {channel.hasCustomProvider && (
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                                Custom Provider
                                            </span>
                                        )}
                                    </div>
                                    {/* Info: custom provider means system policy is ignored */}
                                    {channel.hasCustomProvider && (
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                            {t('entityHasCustomProvider')}
                                        </p>
                                    )}
                                </div>

                                {/* Policy dropdown + save */}
                                <div className="flex items-center gap-2">
                                    <select
                                        value={policy}
                                        onChange={(e) => setPolicies(prev => ({
                                            ...prev,
                                            [entity.id]: e.target.value
                                        }))}
                                        className={cn(
                                            "p-1.5 text-sm border rounded-md",
                                            "border-gray-300 dark:border-gray-600",
                                            "bg-white dark:bg-gray-700",
                                            text.primary
                                        )}
                                    >
                                        <option value="none">{t('smsPolicyNone')}</option>
                                        <option value="us_ca">{t('smsPolicyUsCa')}</option>
                                        <option value="all">{t('smsPolicyAll')}</option>
                                    </select>
                                    <ThemedButton
                                        size="sm"
                                        onClick={() => handleSave(entity.id)}
                                        disabled={!hasChanged || saving[entity.id]}
                                    >
                                        <Save className="w-3.5 h-3.5 mr-1" />
                                        {saving[entity.id] ? '...' : t('save')}
                                    </ThemedButton>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {entities.length === 0 && (
                    <p className={cn("text-center py-8", text.secondary)}>
                        No entities found.
                    </p>
                )}
            </div>
        </div>
    );
};

export default SystemSmsPolicy;
